import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Shield, DollarSign, Receipt, Download, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { documentosAPI } from '../../services/api';
import { guardarBlob, obtenerArchivoAutenticado } from '../../utils/descargaAutenticada';

const TIPOS_DOC = [
  {
    codigo: 'CONTRATO_CREDITO',
    titulo: 'Contrato de Crédito',
    descripcion: 'Apertura de crédito simple con cláusulas contractuales y condiciones de pago',
    icon: FileText,
    endpointGen: 'generarContrato'
  },
  {
    codigo: 'GARANTIA_PRENDARIA',
    titulo: 'GARANTÍA PRENDARIA',
    descripcion: 'Relación de bienes muebles en garantía del crédito',
    icon: Shield,
    endpointGen: 'generarGarantia'
  },
  {
    codigo: 'PAGARE',
    titulo: 'PAGARÉ',
    descripcion: 'Documento de promesa de pago y reconocimiento de saldo contractual',
    icon: DollarSign,
    endpointGen: 'generarPagare'
  },
  {
    codigo: 'RECIBO_DESEMBOLSO',
    titulo: 'Recibo de Entrega de Efectivo',
    descripcion: 'Comprobante formal de recepción de fondos desembolsados',
    icon: Receipt,
    endpointGen: 'generarRecibo'
  }
];

export default function DocumentosTab({ creditoId, credito }) {
  const [documentos, setDocumentos] = useState([]);
  const [garantias, setGarantias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState({});
  const [descargando, setDescargando] = useState(null); // código del documento en descarga

  // Los endpoints de PDF exigen sesión (DOCUMENTOS_VER). Un enlace <a href>
  // no envía el Bearer y recibía 401, así que se descarga con apiClient (blob)
  // y se valida que la respuesta sea un PDF antes de guardarla.
  const handleDescargarPdf = async (codigo, solicitar) => {
    if (descargando) return;
    setDescargando(codigo);
    try {
      const blob = await obtenerArchivoAutenticado('pdf', solicitar);
      guardarBlob(blob, `MIDAS_${codigo}_Credito_${creditoId}.pdf`);
    } catch (err) {
      alert(err.message || 'No se pudo descargar el documento.');
    } finally {
      setDescargando(null);
    }
  };

  // Formulario de nueva garantía
  const [showGarantiaForm, setShowGarantiaForm] = useState(false);
  const [nuevaGarantia, setNuevaGarantia] = useState({
    tipoBien: 'ELECTRODOMÉSTICO',
    descripcion: '',
    marca: '',
    modelo: '',
    numeroSerie: '',
    color: '',
    valorEstimado: ''
  });

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [resDocs, resGarantias] = await Promise.all([
        documentosAPI.listarPorCredito(creditoId),
        documentosAPI.listarGarantias(creditoId)
      ]);
      setDocumentos(resDocs.data?.data || []);
      setGarantias(resGarantias.data?.data || []);
      setError(null);
    } catch (e) {
      setError('No fue posible cargar la documentación contractual');
    } finally {
      setLoading(false);
    }
  }, [creditoId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleGenerar = async (tipoCodigo, endpointName) => {
    try {
      setGenerating(prev => ({ ...prev, [tipoCodigo]: true }));
      await documentosAPI[endpointName](creditoId);
      await cargarDatos();
    } catch (e) {
      setError(`Error al generar ${tipoCodigo}: ${e.response?.data?.error || e.message}`);
    } finally {
      setGenerating(prev => ({ ...prev, [tipoCodigo]: false }));
    }
  };

  const handleFormalizar = async (documentoId) => {
    try {
      setLoading(true);
      await documentosAPI.formalizar(documentoId);
      await cargarDatos();
    } catch (e) {
      setError(`Error al formalizar: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCrearGarantia = async (e) => {
    e.preventDefault();
    if (!nuevaGarantia.descripcion || !nuevaGarantia.valorEstimado) {
      alert('Descripción y valor estimado son obligatorios');
      return;
    }
    try {
      await documentosAPI.crearGarantia(creditoId, {
        ...nuevaGarantia,
        valorEstimado: parseFloat(nuevaGarantia.valorEstimado)
      });
      setShowGarantiaForm(false);
      setNuevaGarantia({
        tipoBien: 'ELECTRODOMÉSTICO',
        descripcion: '',
        marca: '',
        modelo: '',
        numeroSerie: '',
        color: '',
        valorEstimado: ''
      });
      await cargarDatos();
    } catch (err) {
      alert('Error al agregar garantía: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEliminarGarantia = async (garantiaId) => {
    if (!window.confirm('¿Está seguro de eliminar esta garantía prendaria?')) return;
    try {
      await documentosAPI.eliminarGarantia(creditoId, garantiaId);
      await cargarDatos();
    } catch (err) {
      alert('Error al eliminar garantía');
    }
  };

  if (loading && !documentos.length) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        <p>Cargando módulo de documentación contractual...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {error && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      {/* Banner de Revisión Legal */}
      <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <AlertTriangle size={20} color="#d97706" />
        <span style={{ fontSize: '0.85rem', color: '#92400e' }}>
          <strong>Aviso Jurídico:</strong> Las cláusulas y textos de las plantillas se encuentran identificadas como <code>LEGAL_REVIEW_REQUIRED</code> sujetas a validación jurídica definitiva.
        </span>
      </div>

      {/* Contexto de Tipo de Crédito y Agrupación (Fase 14.2) */}
      {(() => {
        const tipoCanonico = (credito?.tipoCredito === 'PERSONAL' || credito?.tipoCredito === 'INDIVIDUAL') ? 'INDIVIDUAL' : (credito?.tipoCredito || 'INDIVIDUAL');
        const nombreAgrupacion = credito?.agrupacion?.nombre || credito?.nombreGrupo || null;
        const folioAgrupacion = credito?.agrupacion?.folio || null;

        return (
          <div style={{
            padding: '14px 18px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b' }}>TIPO DE CRÉDITO:</span>
              <span style={{
                fontSize: '0.82rem',
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: '6px',
                background: tipoCanonico === 'GRUPAL' ? '#f0fdf4' : tipoCanonico === 'COLOCADORA' ? '#fef3c7' : '#eff6ff',
                color: tipoCanonico === 'GRUPAL' ? '#166534' : tipoCanonico === 'COLOCADORA' ? '#92400e' : '#1e40af',
                border: `1px solid ${tipoCanonico === 'GRUPAL' ? '#bbf7d0' : tipoCanonico === 'COLOCADORA' ? '#fde68a' : '#bfdbfe'}`
              }}>
                {tipoCanonico}
              </span>
            </div>

            {tipoCanonico === 'GRUPAL' && (
              <div style={{ fontSize: '0.85rem', color: '#0f766e' }}>
                <strong>Grupo:</strong> {nombreAgrupacion || 'Sin asignar'} {folioAgrupacion && <span style={{ color: '#64748b' }}>({folioAgrupacion})</span>}
              </div>
            )}

            {tipoCanonico === 'COLOCADORA' && (
              <div style={{ fontSize: '0.85rem', color: '#92400e' }}>
                <strong>Colocadora:</strong> {nombreAgrupacion || 'Sin asignar'} {folioAgrupacion && <span style={{ color: '#64748b' }}>({folioAgrupacion})</span>}
              </div>
            )}
          </div>
        );
      })()}

      {/* Grid de 4 Documentos */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        {TIPOS_DOC.map(t => {
          const docExistente = documentos.find(d => d.tipoDocumento === t.codigo);
          const Icon = t.icon;
          const status = docExistente ? docExistente.status : 'NO_GENERADO';

          return (
            <div
              key={t.codigo}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '20px',
                background: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '16px'
              }}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ padding: '8px', background: '#f0fdf4', borderRadius: '8px', color: '#166534' }}>
                    <Icon size={22} />
                  </div>
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '6px',
                    background: status === 'FORMALIZED' ? '#dcfce7' : status === 'GENERATED' ? '#e0f2fe' : status === 'DRAFT' ? '#fef3c7' : '#f1f5f9',
                    color: status === 'FORMALIZED' ? '#166534' : status === 'GENERATED' ? '#0369a1' : status === 'DRAFT' ? '#92400e' : '#64748b'
                  }}>
                    {status}
                  </span>
                </div>

                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                  {t.titulo}
                </h3>
                <p style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4 }}>
                  {t.descripcion}
                </p>

                {docExistente && (
                  <div style={{ marginTop: '12px', fontSize: '0.75rem', color: '#94a3b8' }}>
                    <p>Hash: {docExistente.sha256.slice(0, 16)}...</p>
                    <p>Fecha: {new Date(docExistente.generatedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* Botones de acción */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {!docExistente ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => handleDescargarPdf(t.codigo, () => documentosAPI.descargarPdfPorTipo(creditoId, t.codigo))}
                      disabled={!!descargando}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        background: '#07344A',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        textDecoration: 'none',
                        cursor: descargando ? 'wait' : 'pointer'
                      }}
                    >
                      <Download size={15} /> {descargando === t.codigo ? 'Generando PDF…' : 'Generar y Descargar PDF'}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleGenerar(t.codigo, t.endpointGen)}
                      disabled={generating[t.codigo]}
                      style={{
                        padding: '6px 10px',
                        background: '#f8fafc',
                        color: '#334155',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '0.78rem',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      {generating[t.codigo] ? 'Creando snapshot...' : 'Crear Snapshot / Formalizar'}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => handleDescargarPdf(t.codigo, () => documentosAPI.descargarPdf(docExistente.id))}
                      disabled={!!descargando}
                      style={{
                        flex: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px 10px',
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        color: '#334155',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        textDecoration: 'none',
                        cursor: descargando ? 'wait' : 'pointer'
                      }}
                    >
                      <Download size={15} /> {descargando === t.codigo ? 'Generando PDF…' : (status === 'FORMALIZED' ? 'Reimprimir PDF' : 'Descargar PDF')}
                    </button>

                    {status !== 'FORMALIZED' && (
                      <button
                        type="button"
                        onClick={() => handleFormalizar(docExistente.id)}
                        style={{
                          padding: '8px 12px',
                          background: '#166534',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Formalizar
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sección de Gestión de Garantías Prendarias */}
      <div style={{ marginTop: '16px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1e293b' }}>
              Bienes en Garantía Prendaria ({garantias.length})
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
              Inventario detallado de bienes muebles afectos al crédito
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowGarantiaForm(!showGarantiaForm)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              background: '#07344A',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            <Plus size={16} /> {showGarantiaForm ? 'Cancelar' : 'Agregar Bien'}
          </button>
        </div>

        {showGarantiaForm && (
          <form onSubmit={handleCrearGarantia} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', padding: '16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '16px' }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#475569' }}>Tipo de Bien</label>
              <input
                type="text"
                value={nuevaGarantia.tipoBien}
                onChange={e => setNuevaGarantia({ ...nuevaGarantia, tipoBien: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', marginTop: '4px' }}
                required
              />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#475569' }}>Descripción / Características</label>
              <input
                type="text"
                value={nuevaGarantia.descripcion}
                onChange={e => setNuevaGarantia({ ...nuevaGarantia, descripcion: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', marginTop: '4px' }}
                placeholder="Ej. Pantalla Smart TV 55 pulgadas"
                required
              />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#475569' }}>Marca</label>
              <input
                type="text"
                value={nuevaGarantia.marca}
                onChange={e => setNuevaGarantia({ ...nuevaGarantia, marca: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', marginTop: '4px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#475569' }}>Modelo / Serie</label>
              <input
                type="text"
                value={nuevaGarantia.numeroSerie}
                onChange={e => setNuevaGarantia({ ...nuevaGarantia, numeroSerie: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', marginTop: '4px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#475569' }}>Valor Estimado (MXN)</label>
              <input
                type="number"
                step="0.01"
                value={nuevaGarantia.valorEstimado}
                onChange={e => setNuevaGarantia({ ...nuevaGarantia, valorEstimado: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', marginTop: '4px' }}
                required
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '9px',
                  background: '#166534',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Guardar Bien
              </button>
            </div>
          </form>
        )}

        {garantias.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic', padding: '12px 0' }}>
            No se han registrado bienes prendarios desglosados para este crédito.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px' }}>Tipo</th>
                <th style={{ padding: '8px 6px' }}>Descripción</th>
                <th style={{ padding: '8px 6px' }}>Marca/Modelo</th>
                <th style={{ padding: '8px 6px' }}>Serie</th>
                <th style={{ padding: '8px 6px' }}>Valor Estimado</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {garantias.map(g => (
                <tr key={g.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 6px', fontWeight: 500 }}>{g.tipoBien}</td>
                  <td style={{ padding: '10px 6px' }}>{g.descripcion}</td>
                  <td style={{ padding: '10px 6px' }}>{[g.marca, g.modelo].filter(Boolean).join(' ') || 'N/A'}</td>
                  <td style={{ padding: '10px 6px' }}>{g.numeroSerie || 'N/A'}</td>
                  <td style={{ padding: '10px 6px', fontWeight: 600 }}>${parseFloat(g.valorEstimado).toFixed(2)}</td>
                  <td style={{ padding: '10px 6px', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => handleEliminarGarantia(g.id)}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
